/*
 * Strings for the third door — services for hire — English and es-PR.
 *
 * A table of its own beside strings.ts rather than a hundred more keys in it:
 * strings.ts is already the longest file in the project, and everything here
 * belongs to one feature that can be read (and translated) in one sitting.
 * `sv()` reads the same locale `tw()` does, so the two cannot disagree about
 * which language is on.
 *
 * "Pro" is the user-facing word for a provider: short, not a job title, and it
 * fits a tab. In Spanish it is `profesional` in sentences and `oficio` where
 * the trade itself is meant, which is the word the island uses for plumber,
 * electrician and mason alike.
 */
import { getLocale } from '../lib/i18n'

const EN = {
  'sv.path.title': 'I Need a Pro',
  'sv.path.sub': 'Plumbers, electricians, hauling and more.',

  'sv.switch.now': 'Hiring a pro',
  'sv.switch.to': 'Switch to hiring a pro',

  'sv.tab.find': 'Find a Pro',
  'sv.tab.requests': 'My Requests',
  'sv.tab.saved': 'Saved',
  'sv.saved.none': 'You have not saved anyone yet.',
  'sv.saved.noneHint': 'Tap the bookmark on a pro to keep them here.',
  'sv.tab.ads': 'My Services',

  'sv.feed.heading': 'Find a pro on Vieques',
  'sv.feed.sub': 'Local people who do the work. Ask for help and they call you back.',
  'sv.feed.search': 'Search plumbers, hauling, electricians…',
  'sv.feed.allCategories': 'All kinds of work',
  'sv.feed.none': 'Nothing matches that.',
  'sv.feed.noneAtAll': 'No services listed yet. Check back soon, or list your own.',
  'sv.feed.loading': 'Loading…',
  'sv.feed.loadError': 'The services could not be loaded.',
  'sv.feed.retry': 'Try again',
  'sv.feed.more': 'Show more',
  'sv.feed.listYours': 'Offer your own service',
  'sv.browse.free': 'Browsing is free. Create an account to contact a pro or to list your own service.',
  'sv.browse.join': 'Create account',

  'sv.rate.hour': 'per hour',
  'sv.rate.job': 'per job',
  'sv.rate.trip': 'per trip',
  'sv.rate.day': 'per day',
  'sv.rate.quote': 'Ask for a quote',
  'sv.rate.none': 'Rate not stated',

  'sv.detail.back': 'All services',
  'sv.detail.area': 'Where they work',
  'sv.detail.availability': 'When',
  'sv.detail.about': 'What they do',
  'sv.detail.contact': 'Contact',
  'sv.detail.call': 'Call',
  'sv.detail.whatsapp': 'WhatsApp',
  'sv.detail.signIn': 'Create an account to contact this pro.',
  'sv.detail.own': 'This is your own ad.',
  'sv.detail.notLive': 'This ad is not visible to the public right now.',
  'sv.detail.removed': 'An administrator took this ad down.',
  'sv.detail.loadError': 'That service could not be loaded.',

  'sv.req.title': 'Ask for help',
  'sv.req.message': 'What do you need done?',
  'sv.req.messageHint': 'Where, what, and when you would like it done.',
  'sv.req.phone': 'Your phone number',
  'sv.req.phoneHint': 'So they can call you back. Only this pro sees it.',
  'sv.req.send': 'Send request',
  'sv.req.sent': 'Sent. They will call you back on the number you gave.',
  'sv.req.already': 'You already asked this pro today. They have your number.',
  'sv.req.cancel': 'Cancel request',
  'sv.req.none': 'You have not asked anyone for help yet.',
  'sv.req.noneHint': 'Find a pro and send a request. It will show up here.',
  'sv.req.status.new': 'Waiting for a call back',
  'sv.req.status.handled': 'Answered',
  'sv.req.status.cancelled': 'Cancelled',
  'sv.req.adGone': 'This ad is no longer listed.',

  'sv.ads.heading': 'My services',
  'sv.ads.sub': 'List what you do. People ask, and you call them back.',
  'sv.ads.new': 'List a service',
  'sv.ads.none': 'You have not listed a service yet.',
  'sv.ads.noneHint': 'Plumbing, electrical, hauling, repairs. If you do the work, list it.',
  'sv.ads.requests': 'Requests',
  'sv.ads.noRequests': 'No requests yet.',
  'sv.ads.newCount': 'new',
  'sv.ads.edit': 'Edit',
  'sv.ads.publish': 'Publish',
  'sv.ads.pause': 'Pause',
  'sv.ads.delete': 'Delete',
  'sv.ads.deleteConfirm': 'Delete this ad and its requests? This cannot be undone.',
  'sv.ads.deleteYes': 'Yes, delete',
  'sv.ads.deleteNo': 'Keep it',
  'sv.ads.markHandled': 'Mark answered',
  'sv.ads.markNew': 'Mark as new',
  'sv.ads.status.draft': 'Draft',
  'sv.ads.status.published': 'Live',
  'sv.ads.status.paused': 'Paused',
  'sv.ads.status.removed': 'Removed',
  'sv.ads.removedWhy': 'Removed by an administrator:',

  'sv.form.new': 'List a service',
  'sv.form.title': 'What you do',
  'sv.form.titleExample': 'Junk runs and dump hauls',
  'sv.form.business': 'Business name (optional)',
  'sv.form.category': 'Kind of work',
  'sv.form.description': 'Tell people about it',
  'sv.form.area': 'Where you work',
  'sv.form.areaExample': 'Esperanza, Isabel II, the whole island',
  'sv.form.availability': 'When you are available',
  'sv.form.rate': 'Your rate',
  'sv.form.rateFrom': 'From',
  'sv.form.rateTo': 'To',
  'sv.form.rateUnit': 'Per',
  'sv.form.phone': 'Phone number people can call',
  'sv.form.phoneHint': 'Shown only to people signed in to the app.',
  'sv.form.color': 'Ad colour',
  'sv.form.colorHint': 'The colour of the block on your ad. A logo can replace the icon later.',
  'sv.color.teal': 'Teal',
  'sv.color.coral': 'Coral',
  'sv.color.amber': 'Amber',
  'sv.color.indigo': 'Indigo',
  'sv.color.green': 'Green',
  'sv.color.plum': 'Plum',
  'sv.color.sky': 'Sky',
  'sv.color.slate': 'Slate',
  'sv.form.save': 'Save',
  'sv.form.saved': 'Saved.',
  'sv.form.needs': 'Needs a title, a description and a phone number before it can go live.',
  'sv.form.error': 'That did not save.',
  'sv.pref.talk': 'Tap record and talk like you would to a neighbour. Cover these:',
  'sv.pref.record': 'Record',
  'sv.pref.stop': 'Stop',
  'sv.pref.recording': 'Recording…',
  'sv.pref.again': 'Record again',
  'sv.pref.recorded': 'Recorded',
  'sv.pref.typeInstead': 'Type instead',
  'sv.pref.recordInstead': 'Record instead',
  'sv.pref.micDenied': 'We could not use the microphone. You can type instead.',
  'sv.pref.heard': 'What we heard',
  'sv.pref.tooLong': 'That was longer than we can take. Try again, a little shorter.',
  'sv.pref.badge': 'Preferred',
  'sv.pref.storyTitle': 'In their own words',
  'sv.pref.heading': 'Preferred listing',
  'sv.pref.intro': 'Tell us about your work in your own words. We write it up, you approve it, and it shows on your ad.',
  'sv.pref.start': 'Tell your story',
  'sv.pref.edit': 'Redo it',
  'sv.pref.withdraw': 'Take it down',
  'sv.pref.balance': 'Tokens',
  'sv.pref.cost': 'Writing it up costs',
  'sv.pref.q.what': 'What do you do?',
  'sv.pref.q.where': 'Where do you work?',
  'sv.pref.q.like': 'What do you like about the work?',
  'sv.pref.q.dislike': 'What do you not take on?',
  'sv.pref.q.other': 'Anything else people should know?',
  'sv.pref.hint': 'Talk like you would to a neighbour. You can dictate with the microphone on your keyboard.',
  'sv.pref.write': 'Write it up',
  'sv.pref.writing': 'Writing…',
  'sv.pref.draftTitle': 'Here is how it reads',
  'sv.pref.draftHint': 'It only uses what you said. Change anything you like before you approve it.',
  'sv.pref.approve': 'Approve and show it',
  'sv.pref.retry': 'Try again',
  'sv.pref.cancel': 'Cancel',
  'sv.pref.approved': 'Approved. It is on your ad now.',
  'sv.pref.outOfTokens': 'You are out of tokens.',
  'sv.pref.notReady': 'The writing helper is not switched on yet.',
  'sv.pref.tooShort': 'Tell us a little more about what you do first.',
  'sv.pref.error': 'That did not work. If tokens were taken they were returned.',
} as const

export type ServicesKey = keyof typeof EN

const ES: Record<ServicesKey, string> = {
  'sv.path.title': 'Necesito un profesional',
  'sv.path.sub': 'Plomeros, electricistas, acarreo y más.',

  'sv.switch.now': 'Buscando un profesional',
  'sv.switch.to': 'Cambiar a buscar un profesional',

  'sv.tab.find': 'Buscar',
  'sv.tab.requests': 'Mis pedidos',
  'sv.tab.saved': 'Guardados',
  'sv.saved.none': 'Todavía no has guardado a nadie.',
  'sv.saved.noneHint': 'Toca el marcador en un profesional para guardarlo aquí.',
  'sv.tab.ads': 'Mis servicios',

  'sv.feed.heading': 'Encuentra un profesional en Vieques',
  'sv.feed.sub': 'Gente de aquí que hace el trabajo. Pide ayuda y te devuelven la llamada.',
  'sv.feed.search': 'Busca plomeros, acarreo, electricistas…',
  'sv.feed.allCategories': 'Todo tipo de trabajo',
  'sv.feed.none': 'Nada coincide con eso.',
  'sv.feed.noneAtAll': 'Todavía no hay servicios. Vuelve pronto, o anuncia el tuyo.',
  'sv.feed.loading': 'Cargando…',
  'sv.feed.loadError': 'No se pudieron cargar los servicios.',
  'sv.feed.retry': 'Intentar de nuevo',
  'sv.feed.more': 'Ver más',
  'sv.feed.listYours': 'Ofrece tu propio servicio',
  'sv.browse.free': 'Mirar es gratis. Crea una cuenta para contactar a un profesional o anunciar tu servicio.',
  'sv.browse.join': 'Crear cuenta',

  'sv.rate.hour': 'por hora',
  'sv.rate.job': 'por trabajo',
  'sv.rate.trip': 'por viaje',
  'sv.rate.day': 'por día',
  'sv.rate.quote': 'Pide un estimado',
  'sv.rate.none': 'Tarifa no indicada',

  'sv.detail.back': 'Todos los servicios',
  'sv.detail.area': 'Dónde trabaja',
  'sv.detail.availability': 'Cuándo',
  'sv.detail.about': 'Lo que hace',
  'sv.detail.contact': 'Contacto',
  'sv.detail.call': 'Llamar',
  'sv.detail.whatsapp': 'WhatsApp',
  'sv.detail.signIn': 'Crea una cuenta para contactar a este profesional.',
  'sv.detail.own': 'Este es tu propio anuncio.',
  'sv.detail.notLive': 'Este anuncio no se ve en público ahora mismo.',
  'sv.detail.removed': 'Un administrador quitó este anuncio.',
  'sv.detail.loadError': 'No se pudo cargar ese servicio.',

  'sv.req.title': 'Pide ayuda',
  'sv.req.message': '¿Qué necesitas que hagan?',
  'sv.req.messageHint': 'Dónde, qué y cuándo lo quieres.',
  'sv.req.phone': 'Tu número de teléfono',
  'sv.req.phoneHint': 'Para que te devuelvan la llamada. Solo este profesional lo ve.',
  'sv.req.send': 'Enviar pedido',
  'sv.req.sent': 'Enviado. Te llamarán al número que diste.',
  'sv.req.already': 'Ya le pediste ayuda a este profesional hoy. Tiene tu número.',
  'sv.req.cancel': 'Cancelar pedido',
  'sv.req.none': 'Todavía no le has pedido ayuda a nadie.',
  'sv.req.noneHint': 'Busca un profesional y envía un pedido. Saldrá aquí.',
  'sv.req.status.new': 'Esperando que te llamen',
  'sv.req.status.handled': 'Atendido',
  'sv.req.status.cancelled': 'Cancelado',
  'sv.req.adGone': 'Este anuncio ya no está publicado.',

  'sv.ads.heading': 'Mis servicios',
  'sv.ads.sub': 'Anuncia lo que haces. La gente te pide y tú les devuelves la llamada.',
  'sv.ads.new': 'Anunciar un servicio',
  'sv.ads.none': 'Todavía no has anunciado ningún servicio.',
  'sv.ads.noneHint': 'Plomería, electricidad, acarreo, reparaciones. Si haces el trabajo, anúncialo.',
  'sv.ads.requests': 'Pedidos',
  'sv.ads.noRequests': 'Todavía no hay pedidos.',
  'sv.ads.newCount': 'nuevos',
  'sv.ads.edit': 'Editar',
  'sv.ads.publish': 'Publicar',
  'sv.ads.pause': 'Pausar',
  'sv.ads.delete': 'Borrar',
  'sv.ads.deleteConfirm': '¿Borrar este anuncio y sus pedidos? No se puede deshacer.',
  'sv.ads.deleteYes': 'Sí, borrar',
  'sv.ads.deleteNo': 'Conservar',
  'sv.ads.markHandled': 'Marcar atendido',
  'sv.ads.markNew': 'Marcar como nuevo',
  'sv.ads.status.draft': 'Borrador',
  'sv.ads.status.published': 'Publicado',
  'sv.ads.status.paused': 'Pausado',
  'sv.ads.status.removed': 'Quitado',
  'sv.ads.removedWhy': 'Quitado por un administrador:',

  'sv.form.new': 'Anunciar un servicio',
  'sv.form.title': 'Lo que haces',
  'sv.form.titleExample': 'Acarreo de chatarra y viajes al vertedero',
  'sv.form.business': 'Nombre del negocio (opcional)',
  'sv.form.category': 'Tipo de trabajo',
  'sv.form.description': 'Cuéntale a la gente',
  'sv.form.area': 'Dónde trabajas',
  'sv.form.areaExample': 'Esperanza, Isabel II, toda la isla',
  'sv.form.availability': 'Cuándo estás disponible',
  'sv.form.rate': 'Tu tarifa',
  'sv.form.rateFrom': 'Desde',
  'sv.form.rateTo': 'Hasta',
  'sv.form.rateUnit': 'Por',
  'sv.form.phone': 'Teléfono donde te pueden llamar',
  'sv.form.phoneHint': 'Solo lo ve quien tiene sesión en la app.',
  'sv.form.color': 'Color del anuncio',
  'sv.form.colorHint': 'El color del bloque de tu anuncio. Luego un logo puede reemplazar el ícono.',
  'sv.color.teal': 'Verde azulado',
  'sv.color.coral': 'Coral',
  'sv.color.amber': 'Ámbar',
  'sv.color.indigo': 'Índigo',
  'sv.color.green': 'Verde',
  'sv.color.plum': 'Ciruela',
  'sv.color.sky': 'Cielo',
  'sv.color.slate': 'Pizarra',
  'sv.form.save': 'Guardar',
  'sv.form.saved': 'Guardado.',
  'sv.form.needs': 'Necesita título, descripción y teléfono para poder publicarse.',
  'sv.form.error': 'No se pudo guardar.',
  'sv.pref.talk': 'Toca grabar y habla como le hablarías a un vecino. Cuenta:',
  'sv.pref.record': 'Grabar',
  'sv.pref.stop': 'Parar',
  'sv.pref.recording': 'Grabando…',
  'sv.pref.again': 'Grabar de nuevo',
  'sv.pref.recorded': 'Grabado',
  'sv.pref.typeInstead': 'Escribir en su lugar',
  'sv.pref.recordInstead': 'Grabar en su lugar',
  'sv.pref.micDenied': 'No pudimos usar el micrófono. Puedes escribir.',
  'sv.pref.heard': 'Lo que escuchamos',
  'sv.pref.tooLong': 'Fue más largo de lo que podemos recibir. Intenta de nuevo, un poco más corto.',
  'sv.pref.badge': 'Preferido',
  'sv.pref.storyTitle': 'En sus propias palabras',
  'sv.pref.heading': 'Anuncio preferido',
  'sv.pref.intro': 'Cuéntanos de tu trabajo con tus propias palabras. Lo redactamos, tú lo apruebas y sale en tu anuncio.',
  'sv.pref.start': 'Cuenta tu historia',
  'sv.pref.edit': 'Hacerlo de nuevo',
  'sv.pref.withdraw': 'Quitarlo',
  'sv.pref.balance': 'Fichas',
  'sv.pref.cost': 'Redactarlo cuesta',
  'sv.pref.q.what': '¿Qué haces?',
  'sv.pref.q.where': '¿Dónde trabajas?',
  'sv.pref.q.like': '¿Qué te gusta de tu trabajo?',
  'sv.pref.q.dislike': '¿Qué trabajos no aceptas?',
  'sv.pref.q.other': '¿Algo más que la gente deba saber?',
  'sv.pref.hint': 'Habla como le hablarías a un vecino. Puedes dictar con el micrófono del teclado.',
  'sv.pref.write': 'Redactarlo',
  'sv.pref.writing': 'Redactando…',
  'sv.pref.draftTitle': 'Así quedó',
  'sv.pref.draftHint': 'Solo usa lo que dijiste. Cambia lo que quieras antes de aprobarlo.',
  'sv.pref.approve': 'Aprobar y mostrarlo',
  'sv.pref.retry': 'Intentar de nuevo',
  'sv.pref.cancel': 'Cancelar',
  'sv.pref.approved': 'Aprobado. Ya está en tu anuncio.',
  'sv.pref.outOfTokens': 'Te quedaste sin fichas.',
  'sv.pref.notReady': 'El ayudante de redacción todavía no está activado.',
  'sv.pref.tooShort': 'Cuéntanos un poco más de lo que haces primero.',
  'sv.pref.error': 'No funcionó. Si se cobraron fichas, se devolvieron.',
}

/** The services string table. Same locale as `tw()`. */
export function sv(key: ServicesKey): string {
  return getLocale() === 'es-PR' ? ES[key] : EN[key]
}

/**
 * A rate as a short phrase: "$60–$120 per trip", "Ask for a quote", or "Rate
 * not stated". A quote is a legitimate answer and reads as one rather than as
 * a missing figure.
 */
export function rateLabel(s: { rateMin: number | null; rateMax: number | null; rateUnit: string | null }): string {
  if (s.rateUnit === 'quote') return sv('sv.rate.quote')
  if (s.rateMin === null && s.rateMax === null) return sv('sv.rate.none')
  const money = (n: number) => `$${n.toLocaleString()}`
  const range = s.rateMin !== null && s.rateMax !== null && s.rateMin !== s.rateMax
    ? `${money(s.rateMin)}–${money(s.rateMax)}`
    : money((s.rateMin ?? s.rateMax)!)
  return s.rateUnit ? `${range} ${sv(`sv.rate.${s.rateUnit}` as ServicesKey)}` : range
}
